import {
  type CopilotConfigurationTemplate,
  PolicyEnforcement,
  type ProjectCopilotConfigurationV1Snapshot,
} from '@factoryfixinc/ats-interfaces';

import { CopilotConfigurationTemplatePersistence } from './copilot-configuration-template.persistence';
import { useCopilotConfigurationTemplate } from './copilot-configuration-template.store';
import MeService from '@/core/shared/me/me.service';
import { InternalError } from '@/core/shared/errors/internal.error';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';

export default class CopilotConfigurationTemplateService {
  private store = useCopilotConfigurationTemplate();
  private persistence = new CopilotConfigurationTemplatePersistence();
  private meService = new MeService();

  public get templates(): CopilotConfigurationTemplate[] {
    return this.store.templates;
  }

  public set templates(templates: CopilotConfigurationTemplate[]) {
    this.store.templates = templates;
  }

  public get selectedTemplate(): CopilotConfigurationTemplate | null {
    return this.store.selectedTemplate;
  }

  public set selectedTemplate(template: CopilotConfigurationTemplate | null) {
    this.store.selectedTemplate = template;
  }

  public get defaultCopilotJobCount(): number | null {
    return this.store.defaultCopilotJobCount;
  }

  public async fetchDefaultCopilotJobCount(): Promise<void> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    // Clear first so a failed refresh hides the count instead of showing a stale one
    this.store.defaultCopilotJobCount = null;
    this.store.defaultCopilotJobCount =
      await this.persistence.getDefaultCopilotJobCount(employerId);
  }

  public async fetchTemplates(skip = 0, take = 100): Promise<void> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    this.templates = await this.persistence.getTemplates(employerId, skip, take);
  }

  public async getTemplateById(id: number): Promise<CopilotConfigurationTemplate | undefined> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    return this.persistence.getTemplateById(id, employerId);
  }

  public async createTemplate(
    displayName: string,
    snapshot: ProjectCopilotConfigurationV1Snapshot,
    description?: string,
    candidateAiDisclosurePolicy?: PolicyEnforcement,
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    const template = await this.persistence.createTemplate(employerId, {
      displayName,
      description,
      snapshot: {
        ...snapshot,
        includeAiDisclosure: ProjectCopilotConfigurationUtils.normalizeIncludeAiDisclosure(
          candidateAiDisclosurePolicy,
          snapshot.includeAiDisclosure,
        ),
      },
    });

    return template;
  }

  public async updateTemplate(
    id: number,
    template: {
      displayName?: string;
      description?: string;
      snapshot?: ProjectCopilotConfigurationV1Snapshot;
    },
    candidateAiDisclosurePolicy?: PolicyEnforcement,
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    const updatedTemplate = await this.persistence.updateTemplate(id, employerId, {
      ...template,
      snapshot: template.snapshot
        ? {
            ...template.snapshot,
            includeAiDisclosure: ProjectCopilotConfigurationUtils.normalizeIncludeAiDisclosure(
              candidateAiDisclosurePolicy,
              template.snapshot.includeAiDisclosure,
            ),
          }
        : undefined,
    });

    // Update the selected template if it was the one that was updated
    if (this.selectedTemplate?.id === id && updatedTemplate) {
      this.selectedTemplate = updatedTemplate;
    }

    return updatedTemplate;
  }

  public async transferTemplateOwnership(
    id: number,
    ownerId: number | null,
  ): Promise<CopilotConfigurationTemplate | undefined> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    const updatedTemplate = await this.persistence.transferTemplateOwnership(
      id,
      employerId,
      ownerId,
    );

    // Refresh the templates list
    await this.fetchTemplates();

    // Update the selected template if it was the one that changed hands
    if (this.selectedTemplate?.id === id && updatedTemplate) {
      this.selectedTemplate = updatedTemplate;
    }

    return updatedTemplate;
  }

  public async deleteTemplate(id: number): Promise<void> {
    const employerId = this.meService.employer?.id;

    if (!employerId) {
      throw new InternalError('No employer ID available');
    }

    await this.persistence.deleteTemplate(id, employerId);

    // Clear selected template if it was the one that was deleted
    if (this.selectedTemplate?.id === id) {
      this.selectedTemplate = null;
    }

    // Refresh the templates list
    await this.fetchTemplates();
  }
}
